import api from './api';
import { handleApiError } from '../utils/apiErrorHandler';

export interface DashboardStats {
  totalShipments: number;
  activeDrivers: number;
  totalRevenue: number;
  pendingShipments: number;
  completedShipments: number;
  todayShipments: number;
  monthlyRevenue: number;
  averageDeliveryTime: number;
}

export interface DriverStats {
  totalDeliveries: number;
  completedToday: number;
  currentStatus: 'available' | 'busy' | 'offline';
  rating: number;
  earnings: {
    today: number;
    thisWeek: number;
    thisMonth: number;
  };
  activeShipments: number;
}

export interface RevenueData {
  date: string;
  revenue: number;
  shipments: number;
}

export interface DriverOverview {
  id: number;
  name: string;
  status: 'available' | 'busy' | 'offline';
  location: string;
  rating: number;
  totalDeliveries: number;
  lastActive: string;
}

export interface ShipmentStatusDistribution {
  status: string;
  count: number;
  percentage: number;
}

class DashboardService {
  // Get admin dashboard statistics
  async getAdminStats(): Promise<DashboardStats> {
    try {
      const response = await api.get('/dashboard/admin/stats');
      return response.data;
    } catch (error) {
      // Fallback to individual API calls if dashboard endpoint doesn't exist
      try {
        const [shipmentsRes, driversRes] = await Promise.all([
          api.get('/shipments?limit=1000'),
          api.get('/users?role=driver')
        ]);

        // Handle null data from APIs - ensure we always get arrays
        const shipments = Array.isArray(shipmentsRes.data) 
          ? shipmentsRes.data 
          : (shipmentsRes.data && Array.isArray(shipmentsRes.data.data)) 
            ? shipmentsRes.data.data 
            : [];
        const drivers = Array.isArray(driversRes.data) 
          ? driversRes.data 
          : (driversRes.data && Array.isArray(driversRes.data.data)) 
            ? driversRes.data.data 
            : [];

        // Calculate stats from available data
        const totalShipments = shipments.length;
        const pendingShipments = shipments.filter((s: any) => 
          ['pending', 'in_transit', 'out_for_delivery'].includes(s.status)
        ).length;
        const completedShipments = shipments.filter((s: any) => s.status === 'delivered').length;
        const activeDrivers = drivers.filter((d: any) => d.status === 'available' || d.status === 'busy').length;

        // Calculate revenue (mock calculation if no price data)
        const totalRevenue = shipments.reduce((sum: number, s: any) => {
          return sum + (parseFloat(s.total_price) || 0);
        }, 0);

        const today = new Date().toISOString().split('T')[0];
        const todayShipments = shipments.filter((s: any) => 
          s.created_at && s.created_at.startsWith(today)
        ).length;

        return {
          totalShipments,
          activeDrivers,
          totalRevenue,
          pendingShipments,
          completedShipments,
          todayShipments,
          monthlyRevenue: totalRevenue, // Simplified for now
          averageDeliveryTime: 24 // Mock value in hours
        };
      } catch (fallbackError) {
        throw handleApiError(error, 'Failed to fetch admin dashboard stats');
      }
    }
  }

  // Get driver dashboard statistics
  async getDriverStats(driverId?: number): Promise<DriverStats> {
    try {
      const endpoint = driverId ? `/dashboard/driver/${driverId}/stats` : '/dashboard/driver/stats';
      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      // Fallback implementation
      try {
        const shipmentsRes = await api.get('/drivers/my-shipments');
        const statusRes = await api.get('/drivers/status');
        
        const shipments = Array.isArray(shipmentsRes.data) ? shipmentsRes.data : [];
        const driverInfo = statusRes.data;

        const today = new Date().toISOString().split('T')[0];
        const completedToday = shipments.filter((s: any) => 
          s.status === 'delivered' && s.updated_at?.startsWith(today)
        ).length;

        return {
          totalDeliveries: shipments.filter((s: any) => s.status === 'delivered').length,
          completedToday,
          currentStatus: driverInfo?.status || 'offline',
          rating: driverInfo?.rating || 4.5,
          earnings: {
            today: completedToday * 2500, // Mock calculation
            thisWeek: completedToday * 7 * 2500,
            thisMonth: completedToday * 30 * 2500
          },
          activeShipments: shipments.filter((s: any) => 
            ['pending', 'in_transit', 'out_for_delivery'].includes(s.status)
          ).length
        };
      } catch (fallbackError) {
        throw handleApiError(error, 'Failed to fetch driver dashboard stats');
      }
    }
  }

  // Get revenue data for charts
  async getRevenueData(period: 'week' | 'month' | 'year' = 'month'): Promise<RevenueData[]> {
    try {
      const response = await api.get(`/dashboard/revenue?period=${period}`);
      return response.data;
    } catch (error) {
      // Generate mock data for demonstration
      const mockData: RevenueData[] = [];
      const days = period === 'week' ? 7 : period === 'month' ? 30 : 365;
      
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        
        mockData.push({
          date: date.toISOString().split('T')[0],
          revenue: Math.floor(Math.random() * 50000) + 10000,
          shipments: Math.floor(Math.random() * 20) + 5
        });
      }
      
      return mockData;
    }
  }

  // Get driver overview for admin
  async getDriversOverview(): Promise<DriverOverview[]> {
    try {
      const response = await api.get('/dashboard/drivers/overview');
      return response.data;
    } catch (error) {
      // Fallback to users API
      try {
        const response = await api.get('/users?role=driver');
        // Handle null data from API - ensure we always get an array
        const drivers = Array.isArray(response.data) 
          ? response.data 
          : (response.data && Array.isArray(response.data.data)) 
            ? response.data.data 
            : [];
        
        return drivers.map((driver: any) => ({
          id: driver.id,
          name: driver.name,
          status: driver.status || 'offline',
          location: driver.current_location || 'Unknown',
          rating: driver.rating || 4.0,
          totalDeliveries: driver.total_deliveries || 0,
          lastActive: driver.updated_at || new Date().toISOString()
        }));
      } catch (fallbackError) {
        throw handleApiError(error, 'Failed to fetch drivers overview');
      }
    }
  }

  // Get shipment status distribution
  async getShipmentStatusDistribution(): Promise<ShipmentStatusDistribution[]> {
    try {
      const response = await api.get('/dashboard/shipments/status-distribution');
      return response.data;
    } catch (error) {
      // Fallback calculation
      try {
        const response = await api.get('/shipments?limit=1000');
        // Handle null data from API - ensure we always get an array
        const shipments = Array.isArray(response.data) 
          ? response.data 
          : (response.data && Array.isArray(response.data.data)) 
            ? response.data.data 
            : [];
        
        const statusCounts: { [key: string]: number } = {};
        shipments.forEach((shipment: any) => {
          const status = shipment.status || 'unknown';
          statusCounts[status] = (statusCounts[status] || 0) + 1;
        });

        const total = shipments.length;
        return Object.entries(statusCounts).map(([status, count]) => ({
          status,
          count,
          percentage: total > 0 ? Math.round((count / total) * 100) : 0
        }));
      } catch (fallbackError) {
        throw handleApiError(error, 'Failed to fetch shipment status distribution');
      }
    }
  }

  // Get recent activities
  async getRecentActivities(limit: number = 10): Promise<any[]> {
    try {
      const response = await api.get(`/dashboard/activities?limit=${limit}`);
      return response.data;
    } catch (error) {
      // Return mock activities for now
      return [
        {
          id: 1,
          type: 'shipment_created',
          message: 'New shipment created',
          timestamp: new Date().toISOString(),
          user: 'Admin'
        },
        {
          id: 2,
          type: 'driver_status_changed',
          message: 'Driver status updated to available',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          user: 'Driver John'
        }
      ];
    }
  }
}

export default new DashboardService();
